import React, { useState, useEffect } from 'react';
import { FiUploadCloud, FiX, FiCamera } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import toast from 'react-hot-toast';
import usePrintStore from '../../store/printStore';
import IconWrapper from './IconWrapper';
import { processFile, combineImagesToSinglePdf, createIdCardPdf, isHeicImage, isOfficeDocument } from '../../utils/fileProcessing';
import { Document, DocumentSource } from '../../types/documentTypes';
import { useCameraModal } from '../../context/CameraModalContext';
import { fetchShopDetails } from '../../lib/supabase/api/fetch-shop-details';

const UploadDocumentCard: React.FC = () => {
  const { documents, addDocument, removeDocument, setCurrentStep, selectedShop } = usePrintStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [shopDetails, setShopDetails] = useState<any>(null);
  const { openCameraModal } = useCameraModal();
  
  // Fetch shop details when a shop is selected
  useEffect(() => {
    const getShopDetails = async () => {
      if (selectedShop) {
        try {
          const details = await fetchShopDetails(selectedShop);
          setShopDetails(details);
        } catch (error) {
          console.error('Failed to fetch shop details:', error);
        }
      } else {
        setShopDetails(null);
      }
    };
    
    getShopDetails();
  }, [selectedShop]);
  
  // Check if file is supported
  const isSupportedFileType = (file: File): boolean => {
    const supportedTypes = [
      'application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
      'application/msword', // doc
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // pptx
      'application/vnd.ms-powerpoint', // ppt
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
      'application/vnd.ms-excel', // xls
    ];
    
    // Check by MIME type or by extension for special cases
    return supportedTypes.includes(file.type) || 
           isHeicImage(file) || 
           isOfficeDocument(file);
  };
  
  const handleFileSelect = async (file: File) => {
    if (!isSupportedFileType(file)) {
      toast.error(`Unsupported file type: ${file.name}`, {position: 'top-center' } );
      return false;
    }

    const processedFile = await processFile(file);
    const newDocument: Document = {
      id: `doc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: processedFile.file.name,
      url: processedFile.url,
      file: processedFile.file,
      pageCount: processedFile.pageCount,
      source: 'default' as DocumentSource
    };
    addDocument(newDocument);
    window.scrollTo({ top: 140, behavior: 'smooth' });
    return true;
  };

  const handleCameraCapture = async (capturedImages: File[], mode?: 'idcard' | 'normal') => {
    // Safety check to prevent null/undefined errors
    if (!capturedImages || capturedImages.length === 0) {
      console.log("No images captured or images array is null/undefined");
      return;
    }
    
    console.log("Camera capture called with mode:", mode, "Images:", capturedImages.length);
    
    setIsProcessing(true);
    try {
      // ID Card mode - special processing for front/back images
      // Check for mode and also verify the filenames for extra assurance
      const isIdCardMode = mode === 'idcard' || 
        (capturedImages.length === 2 && 
          (capturedImages[0].name.includes('idcard_front') || 
           capturedImages[1].name.includes('idcard_back')));
      
      if (isIdCardMode && capturedImages.length === 2) {
        console.log("Processing in ID card mode");
        const frontImage = capturedImages[0];
        const backImage = capturedImages[1];
        const currentDate = new Date().toISOString().slice(0, 10);
        
        // Create ID card PDF with front and back images
        const pdfFile = await createIdCardPdf(
          frontImage,
          backImage,
          `id_card_${currentDate}.pdf`
        );

        const processedFile = await processFile(pdfFile);
        const newDocument: Document = {
          id: `doc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: `ID Card - ${currentDate}.pdf`, // More user-friendly name
          url: processedFile.url,
          file: processedFile.file,
          pageCount: processedFile.pageCount,
          source: 'camera' as DocumentSource
        };
        addDocument(newDocument);
        window.scrollTo({ top: 60, behavior: 'smooth' });
        toast.success('ID card processed successfully', { position: 'top-center' });
      } 
      // Standard mode - process single or multiple images
      else if (capturedImages.length === 1) {
        await handleFileSelect(capturedImages[0]);
      } else {
        // For multiple regular images, combine them into one PDF
        const currentDate = new Date().toISOString().slice(0, 10);
        const pdfFile = await combineImagesToSinglePdf(
          capturedImages, 
          `camera_capture_${currentDate}.pdf`
        );

        const processedFile = await processFile(pdfFile);
        const newDocument: Document = {
          id: `doc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: processedFile.file.name,
          url: processedFile.url,
          file: processedFile.file,
          pageCount: processedFile.pageCount,
          source: 'camera' as DocumentSource
        };
        addDocument(newDocument);
        window.scrollTo({ top: 60, behavior: 'smooth' });
        toast.success(`${capturedImages.length} ${capturedImages.length === 1 ? 'image' : 'images'} processed successfully`, { position: 'top-center' });
      }
    } catch (error) {
      console.error('Error processing camera images:', error);
      toast.error('Error processing camera images. Please try again.', { position: 'top-center' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenCamera = () => {
    // Check if the browser supports getUserMedia
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Your browser does not support camera access', { position: 'top-center' });
      return;
    }
    
    // Use the new combined function that sets callback and opens modal in one step
    openCameraModal(handleCameraCapture);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setIsProcessing(true);
      
      try {
        // Process files sequentially to avoid overwhelming the browser
        for (const file of Array.from(files)) {
          await handleFileSelect(file);
        }
      } catch (error) {
        console.error('Error processing files:', error);
        toast.error('Error processing files. Please try again.', { position: 'top-center' });
      } finally {
        setIsProcessing(false);
      }
      
      // Clear the input to allow uploading the same file again
      e.target.value = '';
    }
  };

  const handleRemoveDocument = (id: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent other event handlers
    removeDocument(id);
  };  const handleWhatsAppRedirect = () => {
    const whatsappNumber = '7581023735';
    let message = 'Hi';
    // If a shop is preselected and we have shop details with shop_username, use the prefill message
    if (selectedShop && shopDetails?.shop_username) {
      message = `Hi I am at ${shopDetails.shop_username}`;
    }
    
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setIsProcessing(true);
      
      try {
        for (const file of files) {
          await handleFileSelect(file);
        }
      } catch (error) {
        console.error('Error processing dropped files:', error);
        toast.error('Error processing files. Please try again.', { position: 'top-center' });
      } finally {
        setIsProcessing(false);
      }
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-2xl mx-auto">
      
      <h2 className="text-2xl font-semibold mb-4 text-blue-600">Upload Your Documents</h2>

      <div 
        className={`border-2 border-dashed rounded-lg p-4 sm:p-8 text-center mb-6 transition-colors ${
          isDragOver 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-gray-300'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="flex justify-center mb-3">
          <IconWrapper icon={FiUploadCloud} className="text-4xl sm:text-5xl text-gray-400" />
        </div>
        <p className="text-gray-500 mb-3">Drag and drop your files here, or click to browse</p>
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <label className={`bg-blue-500 text-white py-2 px-4 rounded-lg cursor-pointer hover:bg-blue-600 inline-block w-full sm:w-auto ${
            isProcessing ? 'opacity-50 cursor-not-allowed' : ''
          }`}>
            {isProcessing ? 'Processing...' : 'Browse Files'}
            <input 
              type="file" 
              className="hidden" 
              multiple 
              onChange={handleFileUpload}
              disabled={isProcessing} 
            />
          </label>
          
          <button
            onClick={handleOpenCamera}
            disabled={isProcessing}
            className={`flex items-center justify-center bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 w-full sm:w-auto ${
              isProcessing ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <IconWrapper icon={FiCamera} className="mr-2" />
            Use Camera
          </button>
          
          <button
            onClick={handleWhatsAppRedirect}
            className="flex items-center justify-center bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 w-full sm:w-auto"
          >
            <IconWrapper icon={FaWhatsapp} className="mr-2" />
            WhatsApp
          </button>
        </div>
        <p className="text-gray-400 text-sm mt-3">
          Supported files types: PDF, JPG, JPEG, PNG, HEIC, MS Office Documents
        </p>
        <p className="text-gray-500 text-sm mt-1">Use the camera to scan documents to PDF or capture ID cards (front and back)</p>
        <p className="text-gray-400 text-sm my-2 text-center font-medium">OR</p>
        <p className="text-gray-500 text-sm mt-1">Click on Whatsapp button to upload documents via Whatsapp</p>
      </div>

      {documents.length > 0 && (
        <div className="mb-6">
          <h3 className="font-medium text-lg mb-2">Uploaded Documents ({documents.length})</h3>
          <ul className="divide-y divide-gray-200">
            {documents.map(doc => (
              <li key={doc.id} className="py-2 flex justify-between items-center">
                <div className="truncate flex-1 mr-2">
                  <span>{doc.name}</span>
                  {doc.pageCount && <span className="text-xs text-gray-500 ml-2">({doc.pageCount} {doc.pageCount === 1 ? 'page' : 'pages'})</span>}
                </div>
                <button 
                  onClick={(e) => handleRemoveDocument(doc.id, e)}
                  className="text-gray-500 hover:text-red-500 p-1 rounded-full hover:bg-gray-100"
                  aria-label="Remove document"
                >
                  <IconWrapper icon={FiX} className="text-lg" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
            
      <div className="flex justify-end">
        <button
          onClick={() => {
            setCurrentStep(1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          disabled={documents.length === 0 || isProcessing}
          className={`px-6 py-2 rounded-lg ${
            documents.length === 0 || isProcessing
              ? 'bg-gray-300 cursor-not-allowed no-pulse-animation'
              : 'bg-blue-600 hover:bg-blue-700 text-white subtle-pulse-button'
          }`}
        >
          Continue
        </button>
      </div>

      {/* Removed the local camera modal - it's now rendered at the app root level */}
    </div>
  );
};

export default UploadDocumentCard;
